import { generateLevel1Hexes, getHexCorners, HEX_CONFIG, hexToPixel } from './hexGrid';

let cachedBoardSpan: { width: number; height: number } | null = null;

/** Pixel span of the level-1 board at zoom 1, matching HexGrid's map bounds. */
export function level1BoardSpan(): { width: number; height: number } {
  if (cachedBoardSpan) return cachedBoardSpan;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const hex of generateLevel1Hexes()) {
    const pixel = hexToPixel(hex, 0, 0);
    for (const corner of getHexCorners(pixel, HEX_CONFIG.size)) {
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minY = Math.min(minY, corner.y);
      maxY = Math.max(maxY, corner.y);
    }
  }
  cachedBoardSpan = { width: maxX - minX, height: maxY - minY };
  return cachedBoardSpan;
}

/**
 * Moon disk radius in the same pixels as the canvas bitmap.
 * The disk matches the fitted land-claim board so the cut does not change scale.
 */
export function surveyBoardZoom(viewWidth: number, viewHeight: number): number {
  const span = level1BoardSpan();
  return fitBoardZoom(viewWidth, viewHeight, span.width, span.height);
}

export function surveyMoonRadius(viewWidth: number, viewHeight: number): number {
  const span = level1BoardSpan();
  const zoom = surveyBoardZoom(viewWidth, viewHeight);
  const fitted = Math.max(span.width, span.height) * zoom;
  return Math.min(fitted, Math.min(viewWidth, viewHeight)) / 2;
}

/** Bitmap size for the survey canvas. Square pixels, same cap as the land-claim board. */
export function surveyCanvasSize(viewWidth: number, viewHeight: number, dpr = 1): { width: number; height: number } {
  const pixelRatio = Math.min(Math.max(dpr || 1, 1), 2);
  return {
    width: Math.max(1, Math.round(viewWidth * pixelRatio)),
    height: Math.max(1, Math.round(viewHeight * pixelRatio)),
  };
}

/** Zoom that fits the whole board in the view, with a small margin. */
export function fitBoardZoom(
  viewWidth: number,
  viewHeight: number,
  mapWidth: number,
  mapHeight: number,
): number {
  if (viewWidth <= 0 || viewHeight <= 0 || mapWidth <= 0 || mapHeight <= 0) return 1;
  const fit = Math.min(viewWidth / mapWidth, viewHeight / mapHeight) * 0.92;
  return Math.min(3, Math.max(0.5, fit));
}
